'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function PrivacyPolicyPage() {
  const lastUpdated = 'June 1, 2026';

  const sections = [
    {
      id: 'collection',
      title: '1. Information We Collect',
      content: (
        <div className="space-y-4 text-sm text-slate-fog font-body leading-relaxed">
          <p>Lenders Home LLC collects information necessary to evaluate loan applications, provide our services, and comply with applicable laws. We collect information in the following categories:</p>
          <div>
            <h4 className="font-semibold text-slate mb-2">1.1 Personal Identification Data</h4>
            <p>Full legal name, date of birth, Social Security Number, government-issued identification number, current and previous addresses, phone number, and email address.</p>
          </div>
          <div>
            <h4 className="font-semibold text-slate mb-2">1.2 Financial Data</h4>
            <p>Income information, employment history, bank account details, credit history, existing debt obligations, tax returns, pay stubs, and other financial documentation submitted during the application process.</p>
          </div>
          <div>
            <h4 className="font-semibold text-slate mb-2">1.3 Technical and Usage Data</h4>
            <p>IP address, browser type, operating system, device identifiers, pages visited, time spent on pages, referring URLs, and other technical data collected automatically when you use our platform.</p>
          </div>
          <div>
            <h4 className="font-semibold text-slate mb-2">1.4 Communications Data</h4>
            <p>Records of communications between you and Lenders Home LLC, including emails, chat transcripts, and phone call records (where permitted by law).</p>
          </div>
        </div>
      ),
    },
    {
      id: 'cookies',
      title: '2. Cookies and Tracking Technologies',
      content: (
        <div className="space-y-4 text-sm text-slate-fog font-body leading-relaxed">
          <p>We use cookies and similar tracking technologies to enhance your experience on our platform, analyze usage patterns, and support security functions.</p>
          <div>
            <h4 className="font-semibold text-slate mb-2">Types of Cookies We Use</h4>
            <ul className="space-y-2 ml-4">
              <li><strong className="text-slate">Essential Cookies:</strong> Required for the platform to function. These cannot be disabled without affecting core functionality.</li>
              <li><strong className="text-slate">Analytics Cookies:</strong> Help us understand how users interact with our platform. Data is aggregated and anonymized.</li>
              <li><strong className="text-slate">Security Cookies:</strong> Used to detect and prevent fraudulent activity and unauthorized access.</li>
              <li><strong className="text-slate">Preference Cookies:</strong> Remember your settings and preferences for a more personalized experience.</li>
            </ul>
          </div>
          <p>You may control cookie settings through your browser preferences. Disabling certain cookies may limit platform functionality. We do not use cookies for third-party advertising purposes.</p>
        </div>
      ),
    },
    {
      id: 'usage',
      title: '3. How We Use Your Information',
      content: (
        <div className="space-y-4 text-sm text-slate-fog font-body leading-relaxed">
          <p>We use the information we collect for the following purposes:</p>
          <ul className="space-y-2 ml-4">
            <li><strong className="text-slate">Loan Application Processing:</strong> To evaluate your eligibility, match you with appropriate lending programs, and facilitate the application and underwriting process.</li>
            <li><strong className="text-slate">Identity Verification:</strong> To verify your identity and prevent fraud in compliance with applicable Know Your Customer (KYC) requirements.</li>
            <li><strong className="text-slate">Communications:</strong> To send application status updates, document requests, approval notifications, and other service-related communications.</li>
            <li><strong className="text-slate">Legal Compliance:</strong> To comply with applicable federal and state laws, regulations, and legal processes, including anti-money laundering (AML) obligations.</li>
            <li><strong className="text-slate">Platform Improvement:</strong> To analyze usage patterns, identify technical issues, and improve the functionality and user experience of our platform.</li>
            <li><strong className="text-slate">Security:</strong> To detect, investigate, and prevent fraudulent transactions, unauthorized access, and other security incidents.</li>
          </ul>
        </div>
      ),
    },
    {
      id: 'sharing',
      title: '4. Data Sharing and Disclosure',
      content: (
        <div className="space-y-4 text-sm text-slate-fog font-body leading-relaxed">
          <p>We do not sell your personal information. We may share your information in the following limited circumstances:</p>
          <ul className="space-y-2 ml-4">
            <li><strong className="text-slate">Lending Program Partners:</strong> With your consent, we share your application information with lending program partners to facilitate loan matching and underwriting.</li>
            <li><strong className="text-slate">Service Providers:</strong> With trusted third-party service providers who assist in operating our platform (e.g., cloud hosting, identity verification, payment processing). These providers are contractually bound to protect your data.</li>
            <li><strong className="text-slate">Legal Requirements:</strong> When required by law, court order, or government authority, or to protect the rights, property, or safety of Lenders Home LLC, our users, or the public.</li>
            <li><strong className="text-slate">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction. You will be notified of any such change.</li>
          </ul>
        </div>
      ),
    },
    {
      id: 'security',
      title: '5. Security Measures',
      content: (
        <div className="space-y-4 text-sm text-slate-fog font-body leading-relaxed">
          <p>We implement industry-standard security measures to protect your personal and financial information:</p>
          <ul className="space-y-2 ml-4">
            <li><strong className="text-slate">Encryption:</strong> All data transmitted between your browser and our servers is encrypted using TLS 1.3. Data stored on our servers is encrypted using AES-256.</li>
            <li><strong className="text-slate">Access Controls:</strong> Access to personal data is restricted to authorized personnel on a need-to-know basis. All access is logged and audited.</li>
            <li><strong className="text-slate">Multi-Factor Authentication:</strong> Available for all user accounts and required for administrative access.</li>
            <li><strong className="text-slate">Regular Security Audits:</strong> We conduct regular penetration testing and security audits by qualified third-party security firms.</li>
            <li><strong className="text-slate">Incident Response:</strong> We maintain a documented incident response plan. In the event of a data breach affecting your information, we will notify you as required by applicable law.</li>
          </ul>
          <p>While we employ robust security measures, no system is completely immune to security risks. We encourage you to use strong, unique passwords and enable multi-factor authentication on your account.</p>
        </div>
      ),
    },
    {
      id: 'rights',
      title: '6. Your Rights',
      content: (
        <div className="space-y-4 text-sm text-slate-fog font-body leading-relaxed">
          <p>Depending on your state of residence, you may have the following rights regarding your personal information:</p>
          <ul className="space-y-2 ml-4">
            <li><strong className="text-slate">Right to Access:</strong> Request a copy of the personal information we hold about you.</li>
            <li><strong className="text-slate">Right to Correction:</strong> Request correction of inaccurate or incomplete personal information.</li>
            <li><strong className="text-slate">Right to Deletion:</strong> Request deletion of your personal information, subject to legal retention requirements.</li>
            <li><strong className="text-slate">Right to Portability:</strong> Request your data in a structured, machine-readable format.</li>
            <li><strong className="text-slate">Right to Opt-Out:</strong> Opt out of certain data processing activities, including marketing communications.</li>
            <li><strong className="text-slate">Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights.</li>
          </ul>
          <p>To exercise any of these rights, contact us at <strong className="text-slate">privacy@lendershomellc.com</strong>. We will respond within 30 days of receiving your request.</p>
        </div>
      ),
    },
    {
      id: 'retention',
      title: '7. Data Retention',
      content: (
        <div className="space-y-4 text-sm text-slate-fog font-body leading-relaxed">
          <p>We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, comply with legal obligations, resolve disputes, and enforce our agreements.</p>
          <p>Loan application data is typically retained for a minimum of 7 years following the completion or denial of a loan, as required by federal lending regulations. Account data is retained for the duration of your account and for a period thereafter as required by law.</p>
          <p>You may request deletion of your data at any time. We will honor such requests to the extent permitted by applicable law and our legal obligations.</p>
        </div>
      ),
    },
    {
      id: 'contact',
      title: '8. Contact Information',
      content: (
        <div className="space-y-4 text-sm text-slate-fog font-body leading-relaxed">
          <p>For privacy-related inquiries, requests, or concerns, contact our Privacy Team:</p>
          <div className="bg-cloud rounded-xl p-4 border border-slate-mist/30">
            <p><strong className="text-slate">Lenders Home LLC — Privacy Team</strong></p>
            <p>Email: privacy@lendershomellc.com</p>
            <p>Mailing Address: Lenders Home LLC, Privacy Department, United States</p>
            <p>Response Time: Within 30 business days</p>
          </div>
          <p>If you believe your privacy rights have been violated, you may also file a complaint with the Consumer Financial Protection Bureau (CFPB) or your state's attorney general office.</p>
        </div>
      ),
    },
  ];

  const [activeSection, setActiveSection] = useState(sections?.[0]?.id);

  return (
    <main className="min-h-screen bg-cloud">
      <Header />
      <section className="pt-28 pb-16 px-6 lg:px-10 bg-white border-b border-slate-mist/30">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-light text-sky-dark text-xs font-semibold font-body mb-6 tracking-wide uppercase">
            Legal
          </div>
          <h1 className="font-heading text-display font-bold text-slate-dark leading-tight mb-4">Privacy Policy</h1>
          <p className="text-slate-fog font-body">Last Updated: {lastUpdated} | Effective Date: {lastUpdated}</p>
          <p className="text-slate-fog font-body mt-3 max-w-3xl leading-relaxed">
            This Privacy Policy describes how Lenders Home LLC ("we," "us," or "our") collects, uses, shares, and protects your personal information when you use our platform and services. Please read this policy carefully.
          </p>
        </div>
      </section>
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-card border border-slate-mist/30 p-4 sticky top-24">
              <div className="text-xs font-semibold text-slate-fog font-body uppercase tracking-wide mb-3 px-2">Sections</div>
              <nav className="space-y-1">
                {sections?.map((s) => (
                  <button
                    key={s?.id}
                    onClick={() => setActiveSection(s?.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-body transition-all duration-200 ${
                      activeSection === s?.id
                        ? 'bg-sky text-white font-semibold' :'text-slate-fog hover:bg-cloud hover:text-slate'
                    }`}
                  >
                    {s?.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>
          <div className="flex-1 min-w-0">
            {sections?.map((s) => (
              <div
                key={s?.id}
                id={s?.id}
                className={`bg-white rounded-3xl shadow-card border border-slate-mist/30 p-8 mb-6 ${activeSection === s?.id ? 'ring-2 ring-sky/20' : ''}`}
                onClick={() => setActiveSection(s?.id)}
              >
                <h2 className="font-heading text-xl font-bold text-slate-dark mb-5">{s?.title}</h2>
                {s?.content}
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
